import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import {
  LayoutDashboard,
  CreditCard,
  Settings,
  Users,
  MessageSquare,
  Palette,
  Globe,
  DollarSign,
  TrendingUp,
  Package,
  LogOut,
  Shield,
  Bell,
  Crown,
  UserPlus,
  Percent,
  Languages,
  Brush,
  Eye,
  Edit,
  Trash2,
  Plus,
  Inbox,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Truck,
  Download
} from "lucide-react";
import PaymentSettings from "./PaymentSettings";
import TemplateManager from "./TemplateManager";
import MLMConfiguration from "./MLMConfiguration";
import ChatManagement from "./ChatManagement";
import ThemeCustomizer from "./ThemeCustomizer";
import CardCanvas from "../CardCanvas";
import TemplatesGallery from "./TemplatesGallery";
import TemplateEditor from "./TemplateEditor";
import AssetLibrary from "./AssetLibrary";
import BrandPairings from "./BrandPairings";
import { ordersApi, cardsApi } from "../../services/api";
import { mockAdminSettings, mockOrders, mockResellers, mockProducts, mockApi } from "../../mock";

const EnhancedAdminDashboard = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const [resellers, setResellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  // Permission check — only general admin (permissions=['all']) can edit emails
  const currentAdmin = (() => {
    try { return JSON.parse(localStorage.getItem('admin_user') || '{}'); }
    catch { return {}; }
  })();
  const canEditEmails = Array.isArray(currentAdmin.permissions) && currentAdmin.permissions.includes('all');

  // User CRUD state
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userDraft, setUserDraft] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '',
    userType: 'reseller', resellerLevel: 'Bronze', isActive: true
  });

  // WPS-001 · Pre-Production Queue state · FIFO order (oldest first → print first)
  const [reviewQueue, setReviewQueue] = useState([]);
  const [queueCounts, setQueueCounts] = useState({ reviewPending: 0, inProduction: 0, shipped: 0, counts: {} });
  const [queueFilter, setQueueFilter] = useState('pending'); // pending | approved | in_production | shipped
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [transitionDialog, setTransitionDialog] = useState({ open: false, action: null });
  const [transitionNotes, setTransitionNotes] = useState('');
  const [transitionTracking, setTransitionTracking] = useState('');
  const [transitionLoading, setTransitionLoading] = useState(false);

  const FILTER_STATUSES = {
    pending:       ['submitted', 'pre_production'],
    approved:      ['approved'],
    in_production: ['in_production'],
    shipped:       ['shipped'],
  };

  const loadReviewQueue = async () => {
    const statuses = FILTER_STATUSES[queueFilter] || FILTER_STATUSES.pending;
    const [list, counts] = await Promise.all([
      ordersApi.listByStatus(statuses),
      ordersApi.queueCounts(),
    ]);
    // FIFO: backend returns by createdAt DESC, so reverse for oldest-first
    const sorted = (list || []).slice().sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
    setReviewQueue(sorted);
    if (counts) setQueueCounts(counts);
  };

  const daysSince = (iso) => {
    if (!iso) return null;
    const d = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
    return Math.floor(d);
  };

  const agingBadge = (order) => {
    const d = daysSince(order.createdAt);
    if (d === null) return null;
    if (d >= 3) return { text: `${d}d old · URGENT`, bg: '#fee2e2', color: '#991b1b' };
    if (d >= 1) return { text: `${d}d old`, bg: '#fef3c7', color: '#92400e' };
    return { text: 'today', bg: '#dcfce7', color: '#166534' };
  };

  const statusBadge = (s) => {
    const map = {
      submitted:       { label: 'New · awaiting review', bg: '#fef3c7', color: '#92400e' },
      pre_production:  { label: 'In review',              bg: '#dbeafe', color: '#1e40af' },
      approved:        { label: 'Ready for 4over',        bg: '#dcfce7', color: '#166534' },
      request_changes: { label: 'Changes requested',      bg: '#fed7aa', color: '#9a3412' },
      in_production:   { label: 'At print broker',        bg: '#fde68a', color: '#854d0e' },
      shipped:         { label: 'Shipped',                bg: '#bfdbfe', color: '#1e3a8a' },
      completed:       { label: 'Delivered',              bg: '#d1fae5', color: '#065f46' },
      rejected:        { label: 'Rejected',               bg: '#fecaca', color: '#7f1d1d' },
      cancelled:       { label: 'Cancelled',              bg: '#e5e7eb', color: '#374151' },
    };
    return map[s] || { label: s, bg: '#e5e7eb', color: '#374151' };
  };

  const openOrderReview = async (order) => {
    setSelectedOrder(order);
    // Try to load the business card from first item
    const firstItem = (order.items || [])[0];
    const cardId = firstItem?.businessCardId;
    if (cardId) {
      const card = await cardsApi.get(cardId);
      setSelectedCard(card);
    } else {
      setSelectedCard(null);
    }
    // Auto-transition submitted → pre_production when admin opens it
    if (order.status === 'submitted') {
      await ordersApi.transition(order.id, 'pre_production', `Opened by admin for review`);
      await loadReviewQueue();
    }
  };

  const openTransitionDialog = (action) => {
    setTransitionDialog({ open: true, action });
    setTransitionNotes('');
    setTransitionTracking('');
  };

  const submitTransition = async () => {
    if (!selectedOrder || !transitionDialog.action) return;
    setTransitionLoading(true);
    const map = {
      approve: 'approved',
      request_changes: 'request_changes',
      reject: 'rejected',
      mark_in_production: 'in_production',
      mark_shipped: 'shipped',
    };
    const toStatus = map[transitionDialog.action];
    if (!toStatus) { setTransitionLoading(false); return; }
    const tracking = transitionDialog.action === 'mark_shipped' ? transitionTracking.trim() : null;
    const result = await ordersApi.transition(selectedOrder.id, toStatus, transitionNotes.trim() || null, tracking);
    setTransitionLoading(false);
    setTransitionDialog({ open: false, action: null });
    if (result?.success) {
      setSelectedOrder(null);
      setSelectedCard(null);
      await loadReviewQueue();
    }
  };

  useEffect(() => {
    if (activeTab === 'preproduction') {
      loadReviewQueue();
    }
  }, [activeTab, queueFilter]);

  const reloadResellers = async () => {
    const rs = await mockApi.fetchResellers();
    setResellers(rs || []);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [rs, ps, os, an] = await Promise.all([
        mockApi.fetchResellers(),
        mockApi.getProducts().then(r => r?.data || []),
        mockApi.fetchOrders(),
        mockApi.fetchAnalytics()
      ]);
      setResellers(rs || []);
      setProducts(ps || []);
      setOrders(os || []);
      setAnalytics(an);
      setLoading(false);
    })();
  }, []);

  const openCreateUser = () => {
    setEditingUser(null);
    setUserDraft({ firstName: '', lastName: '', email: '', phone: '', password: '', userType: 'reseller', resellerLevel: 'Bronze', isActive: true });
    setShowUserDialog(true);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserDraft({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '',
      userType: user.userType || 'customer',
      resellerLevel: user.resellerLevel || 'Bronze',
      isActive: user.isActive !== false
    });
    setShowUserDialog(true);
  };

  const handleSaveUser = async () => {
    if (!userDraft.email) {
      alert('Email is required');
      return;
    }
    if (editingUser) {
      const updatePayload = { ...userDraft };
      if (!updatePayload.password) delete updatePayload.password;
      const r = await mockApi.updateUser(editingUser.id, updatePayload);
      if (r.success) {
        await reloadResellers();
        setShowUserDialog(false);
        alert('User updated successfully');
      } else {
        alert('Update failed');
      }
    } else {
      if (!userDraft.password) {
        alert('Password is required for new users');
        return;
      }
      const r = await mockApi.createUser(userDraft);
      if (r.success) {
        await reloadResellers();
        setShowUserDialog(false);
        alert('User created successfully');
      } else {
        alert('Create failed (user with this email may already exist)');
      }
    }
  };

  const handleDeleteUser = async (user) => {
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    const r = await mockApi.deleteUser(user.id);
    if (r.success) {
      await reloadResellers();
      alert('User deleted');
    } else {
      alert('Delete failed');
    }
  };

  // Product CRUD
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productDraft, setProductDraft] = useState({
    name: '', description: '', category: 'Business Cards',
    basePrice: 0, resellerPrice: 0, commissionRate: 25,
    isActive: true
  });

  const reloadProducts = async () => {
    const r = await mockApi.getProducts();
    setProducts(r?.data || []);
  };

  const openCreateProduct = () => {
    setEditingProduct(null);
    setProductDraft({ name: '', description: '', category: 'Business Cards', basePrice: 0, resellerPrice: 0, commissionRate: 25, isActive: true });
    setShowProductDialog(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductDraft({
      name: product.name || '',
      description: product.description || '',
      category: product.category || 'Business Cards',
      basePrice: product.basePrice ?? 0,
      resellerPrice: product.resellerPrice ?? 0,
      commissionRate: product.commissionRate ?? 25,
      isActive: product.isActive !== false
    });
    setShowProductDialog(true);
  };

  const handleSaveProduct = async () => {
    if (!productDraft.name) {
      alert('Product name required');
      return;
    }
    if (editingProduct) {
      const r = await mockApi.updateProduct(editingProduct.id, productDraft);
      if (r.success) {
        await reloadProducts();
        setShowProductDialog(false);
        alert('Product updated');
      } else alert('Update failed');
    } else {
      const r = await mockApi.addProduct(productDraft);
      if (r.success) {
        await reloadProducts();
        setShowProductDialog(false);
        alert('Product created');
      } else alert('Create failed');
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Delete product "${product.name}"?`)) return;
    const r = await mockApi.deleteProduct(product.id);
    if (r.success) {
      await reloadProducts();
      alert('Product deleted');
    } else alert('Delete failed');
  };

  // Add-ons state
  const [addons, setAddons] = useState([]);
  const [showAddonDialog, setShowAddonDialog] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [addonDraft, setAddonDraft] = useState({
    name: '', description: '', price: 0, cost: 0, category: 'finish', isActive: true
  });

  // Partners state
  const [partners, setPartners] = useState([]);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [partnerDraft, setPartnerDraft] = useState({
    businessName: '', contactName: '', email: '', phone: '',
    type: 'brand_consultant', wholesaleDiscount: 20, minMonthlyOrders: 5, isActive: true
  });

  const reloadAddons = async () => setAddons(await mockApi.fetchAddons());
  const reloadPartners = async () => setPartners(await mockApi.fetchPartners());

  useEffect(() => { reloadAddons(); reloadPartners(); }, []);

  const openCreateAddon = () => {
    setEditingAddon(null);
    setAddonDraft({ name: '', description: '', price: 0, cost: 0, category: 'finish', isActive: true });
    setShowAddonDialog(true);
  };
  const openEditAddon = (a) => {
    setEditingAddon(a);
    setAddonDraft({
      name: a.name || '', description: a.description || '',
      price: a.price ?? 0, cost: a.cost ?? 0,
      category: a.category || 'finish', isActive: a.isActive !== false
    });
    setShowAddonDialog(true);
  };
  const handleSaveAddon = async () => {
    if (!addonDraft.name) { alert('Name required'); return; }
    const r = editingAddon
      ? await mockApi.updateAddon(editingAddon.id, addonDraft)
      : await mockApi.createAddon(addonDraft);
    if (r.success) { await reloadAddons(); setShowAddonDialog(false); }
    else alert('Save failed');
  };
  const handleDeleteAddon = async (a) => {
    if (!window.confirm(`Delete add-on "${a.name}"?`)) return;
    const r = await mockApi.deleteAddon(a.id);
    if (r.success) await reloadAddons();
  };

  const openCreatePartner = () => {
    setEditingPartner(null);
    setPartnerDraft({ businessName: '', contactName: '', email: '', phone: '', type: 'brand_consultant', wholesaleDiscount: 20, minMonthlyOrders: 5, isActive: true });
    setShowPartnerDialog(true);
  };
  const openEditPartner = (p) => {
    setEditingPartner(p);
    setPartnerDraft({
      businessName: p.businessName || '', contactName: p.contactName || '',
      email: p.email || '', phone: p.phone || '',
      type: p.type || 'brand_consultant',
      wholesaleDiscount: p.wholesaleDiscount ?? 20,
      minMonthlyOrders: p.minMonthlyOrders ?? 5,
      isActive: p.isActive !== false
    });
    setShowPartnerDialog(true);
  };
  const handleSavePartner = async () => {
    if (!partnerDraft.businessName || !partnerDraft.email) { alert('Business name + email required'); return; }
    const r = editingPartner
      ? await mockApi.updatePartner(editingPartner.id, partnerDraft)
      : await mockApi.createPartner(partnerDraft);
    if (r.success) { await reloadPartners(); setShowPartnerDialog(false); }
    else alert('Save failed');
  };
  const handleDeletePartner = async (p) => {
    if (!window.confirm(`Delete partner "${p.businessName}"?`)) return;
    const r = await mockApi.deletePartner(p.id);
    if (r.success) await reloadPartners();
  };

  const [systemSettings, setSystemSettings] = useState({
    companyName: "Wunli.com Print Studio",
    primaryColor: "#10b981",
    secondaryColor: "#3b82f6",
    logo: "/logo.png",
    language: "en",
    currency: "USD",
    resellerUpgradeFee: 250.00,
    enableMLM: true,
    enableChat: true
  });

  const [discountCodes, setDiscountCodes] = useState([
    { code: "SAVE100", discount: 100, type: "percentage", active: true, secretCode: "ADMIN100" },
    { code: "SAVE50", discount: 50, type: "percentage", active: true, secretCode: "ADMIN50" },
    { code: "SAVE20", discount: 20, type: "percentage", active: true, secretCode: "ADMIN20" }
  ]);

  const stats = [
    {
      title: "Total Revenue",
      value: "$47,892",
      change: "+18.5%",
      icon: <DollarSign className="h-6 w-6 text-green-600" />,
      color: "green"
    },
    {
      title: "Active Resellers",
      value: "234",
      change: "+12.3%",
      icon: <Crown className="h-6 w-6 text-yellow-600" />,
      color: "yellow"
    },
    {
      title: "Cards Generated",
      value: "5,847",
      change: "+8.2%",
      icon: <CreditCard className="h-6 w-6 text-blue-600" />,
      color: "blue"
    },
    {
      title: "Support Tickets",
      value: "47",
      change: "-15.7%",
      icon: <MessageSquare className="h-6 w-6 text-purple-600" />,
      color: "purple"
    }
  ];

  const handleLanguageChange = (lang) => {
    setCurrentLanguage(lang);
    // In real app, this would trigger translation system
    console.log("Language changed to:", lang);
  };

  const handleSystemSettingChange = (key, value) => {
    setSystemSettings(prev => ({ ...prev, [key]: value }));
  };

  const translations = {
    en: {
      welcome: "Welcome back, Administrator",
      overview: "System Overview",
      totalRevenue: "Total Revenue",
      activeResellers: "Active Resellers",
      cardsGenerated: "Cards Generated",
      supportTickets: "Support Tickets"
    },
    es: {
      welcome: "Bienvenido de vuelta, Administrador",
      overview: "Resumen del Sistema",
      totalRevenue: "Ingresos Totales",
      activeResellers: "Revendedores Activos",
      cardsGenerated: "Tarjetas Generadas",
      supportTickets: "Tickets de Soporte"
    }
  };

  const t = translations[currentLanguage];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Enhanced Navigation */}
      <nav className="bg-white/90 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-emerald-500 to-blue-600 p-2 rounded-lg">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <span className="text-xl font-bold text-gray-900">{systemSettings.companyName}</span>
                  <div className="text-xs text-gray-500">Administrator Panel</div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Language Switcher */}
              <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
                <Button
                  size="sm"
                  variant={currentLanguage === "en" ? "default" : "ghost"}
                  className="h-8 px-3"
                  onClick={() => handleLanguageChange("en")}
                >
                  <Languages className="h-3 w-3 mr-1" />
                  EN
                </Button>
                <Button
                  size="sm"
                  variant={currentLanguage === "es" ? "default" : "ghost"}
                  className="h-8 px-3"
                  onClick={() => handleLanguageChange("es")}
                >
                  <Languages className="h-3 w-3 mr-1" />
                  ES
                </Button>
              </div>

              <Button variant="ghost" size="sm">
                <Bell className="h-4 w-4" />
                <Badge className="ml-1 h-5 w-5 p-0 text-xs">3</Badge>
              </Button>
              
              <Link to="/">
                <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.welcome}</h1>
          <p className="text-gray-600">Complete system control and management center</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full bg-white shadow-sm border mb-8 overflow-x-auto" style={{ display: 'grid', gridTemplateColumns: 'repeat(15, minmax(0, 1fr))' }}>
            <TabsTrigger value="overview" className="flex items-center space-x-1">
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden md:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="preproduction" className="flex items-center space-x-1 relative">
              <Inbox className="h-4 w-4" />
              <span className="hidden md:inline">Pre-Production</span>
              {queueCounts.reviewPending > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  background: '#dc2626',
                  color: '#fff',
                  borderRadius: '999px',
                  fontSize: '9px',
                  padding: '1px 5px',
                  fontWeight: 700,
                  fontFamily: 'JetBrains Mono, monospace',
                  minWidth: '16px',
                  textAlign: 'center',
                }}>{queueCounts.reviewPending}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="mlm" className="flex items-center space-x-1">
              <Crown className="h-4 w-4" />
              <span className="hidden md:inline">MLM</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center space-x-1">
              <Package className="h-4 w-4" />
              <span className="hidden md:inline">Products</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center space-x-1">
              <Brush className="h-4 w-4" />
              <span className="hidden md:inline">Templates</span>
            </TabsTrigger>
            <TabsTrigger value="tpleditor" className="flex items-center space-x-1">
              <Edit className="h-4 w-4" />
              <span className="hidden md:inline">Editor</span>
            </TabsTrigger>
            <TabsTrigger value="assets" className="flex items-center space-x-1">
              <Eye className="h-4 w-4" />
              <span className="hidden md:inline">Assets</span>
            </TabsTrigger>
            <TabsTrigger value="pairings" className="flex items-center space-x-1">
              <Crown className="h-4 w-4" />
              <span className="hidden md:inline">Looks</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center space-x-1">
              <Users className="h-4 w-4" />
              <span className="hidden md:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex items-center space-x-1">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden md:inline">Chat</span>
            </TabsTrigger>
            <TabsTrigger value="theme" className="flex items-center space-x-1">
              <Palette className="h-4 w-4" />
              <span className="hidden md:inline">Theme</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center space-x-1">
              <CreditCard className="h-4 w-4" />
              <span className="hidden md:inline">Payments</span>
            </TabsTrigger>
            <TabsTrigger value="addons" className="flex items-center space-x-1">
              <Plus className="h-4 w-4" />
              <span className="hidden md:inline">Add-ons</span>
            </TabsTrigger>
            <TabsTrigger value="partners" className="flex items-center space-x-1">
              <Crown className="h-4 w-4" />
              <span className="hidden md:inline">Partners</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center space-x-1">
              <Settings className="h-4 w-4" />
              <span className="hidden md:inline">Settings</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <Card key={index} className="hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{t[stat.title.toLowerCase().replace(/\s+/g, '')] || stat.title}</p>
                        <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                        <p className={`text-sm text-${stat.color}-600 flex items-center mt-1`}>
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {stat.change}
                        </p>
                      </div>
                      <div className={`bg-${stat.color}-100 p-3 rounded-lg`}>
                        {stat.icon}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* System Status */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>System Health</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Payment Processing</span>
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">AI Chat Support</span>
                      <Badge className="bg-green-100 text-green-800">Online</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">MLM Commission System</span>
                      <Badge className="bg-green-100 text-green-800">Processing</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Template Generation</span>
                      <Badge className="bg-green-100 text-green-800">Available</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Social Media Integration</span>
                      <Badge className="bg-yellow-100 text-yellow-800">Limited</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Button className="w-full justify-start" variant="outline">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create Support Team Member
                    </Button>
                    <Button className="w-full justify-start" variant="outline">
                      <Percent className="h-4 w-4 mr-2" />
                      Generate Discount Code
                    </Button>
                    <Button className="w-full justify-start" variant="outline">
                      <Globe className="h-4 w-4 mr-2" />
                      Update System Settings
                    </Button>
                    <Button className="w-full justify-start" variant="outline">
                      <Eye className="h-4 w-4 mr-2" />
                      View Live Chat Sessions
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Discount Codes Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Percent className="h-5 w-5 text-green-600" />
                  <span>Secret Discount Codes</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {discountCodes.map((code, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <Badge variant={code.active ? "default" : "secondary"}>
                          {code.discount}% OFF
                        </Badge>
                        <div>
                          <p className="font-medium">Code: {code.code}</p>
                          <p className="text-sm text-gray-600">Secret: {code.secretCode}</p>
                        </div>
                      </div>
                      <Switch
                        checked={code.active}
                        onCheckedChange={(checked) => {
                          const updated = [...discountCodes];
                          updated[index].active = checked;
                          setDiscountCodes(updated);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MLM Configuration Tab */}
          {/* WPS-001 · Pre-Production Queue · FIFO (oldest first) */}
          <TabsContent value="preproduction" className="space-y-6">
            {!selectedOrder && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Inbox className="h-5 w-5 text-blue-600" />
                        Pre-Production Queue · FIFO
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        Oldest paid order first · the earlier the customer paid, the earlier we print
                      </p>
                    </div>
                  </div>

                  {/* Filter tabs · clickable to switch view */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                    {[
                      { key: 'pending',       label: 'Pending Review',  count: queueCounts.reviewPending || 0, color: '#dc2626' },
                      { key: 'approved',      label: 'Approved',        count: queueCounts.counts?.approved || 0, color: '#16a34a' },
                      { key: 'in_production', label: 'At 4over',        count: queueCounts.counts?.in_production || 0, color: '#d97706' },
                      { key: 'shipped',       label: 'Shipped',         count: queueCounts.counts?.shipped || 0, color: '#2563eb' },
                    ].map(f => (
                      <button
                        key={f.key}
                        onClick={() => setQueueFilter(f.key)}
                        style={{
                          padding: '12px 16px',
                          background: queueFilter === f.key ? '#0f172a' : '#ffffff',
                          color: queueFilter === f.key ? '#ffffff' : '#0f172a',
                          border: `1px solid ${queueFilter === f.key ? '#0f172a' : '#e2e8f0'}`,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <div style={{
                          fontSize: '24px',
                          fontWeight: 700,
                          color: queueFilter === f.key ? '#ffffff' : f.color,
                          lineHeight: 1,
                        }}>{f.count}</div>
                        <div style={{
                          fontSize: '10px',
                          fontFamily: 'JetBrains Mono, monospace',
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          marginTop: '4px',
                          opacity: 0.85,
                        }}>{f.label}</div>
                      </button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  {reviewQueue.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Inbox className="h-12 w-12 mx-auto mb-3 opacity-40" />
                      <p className="font-medium">
                        {queueFilter === 'pending'      && 'No orders pending review'}
                        {queueFilter === 'approved'     && 'No orders ready for 4over upload'}
                        {queueFilter === 'in_production'&& 'No orders currently at 4over'}
                        {queueFilter === 'shipped'      && 'No shipped orders yet'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reviewQueue.map((order, idx) => {
                        const sBadge = statusBadge(order.status);
                        const aBadge = agingBadge(order);
                        const days = daysSince(order.createdAt);
                        return (
                          <div
                            key={order.id}
                            className="border border-gray-200 hover:border-blue-400 hover:shadow-sm transition-all cursor-pointer p-4 flex items-center justify-between gap-4"
                            onClick={() => openOrderReview(order)}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="font-mono text-xs uppercase tracking-wider text-blue-600 font-bold">
                                  #{idx + 1}
                                </span>
                                <span className="font-mono text-xs uppercase tracking-wider text-gray-500">
                                  {order.id?.slice(0, 8)}
                                </span>
                                <span style={{
                                  background: sBadge.bg, color: sBadge.color,
                                  fontSize: '10px', padding: '2px 8px',
                                  letterSpacing: '0.08em', textTransform: 'uppercase',
                                  fontFamily: 'JetBrains Mono, monospace',
                                }}>{sBadge.label}</span>
                                {aBadge && (
                                  <span style={{
                                    background: aBadge.bg, color: aBadge.color,
                                    fontSize: '10px', padding: '2px 8px',
                                    letterSpacing: '0.08em', textTransform: 'uppercase',
                                    fontFamily: 'JetBrains Mono, monospace', fontWeight: 700,
                                  }}>{aBadge.text}</span>
                                )}
                              </div>
                              <div className="font-semibold text-gray-900">{order.customerName}</div>
                              <div className="text-sm text-gray-600">{order.customerEmail}</div>
                              <div className="text-xs text-gray-500 mt-1 font-mono">
                                {(order.items || []).length} item(s) · ${order.total?.toFixed(2)} · paid {new Date(order.createdAt).toLocaleString()}
                                {order.trackingNumber && ` · ${order.trackingNumber}`}
                              </div>
                            </div>
                            <Button variant="outline" size="sm">
                              <Eye className="h-4 w-4 mr-1" />
                              {queueFilter === 'pending' ? 'Review' : 'Open'}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {selectedOrder && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <CardTitle>Review Order · {selectedOrder.id?.slice(0, 8)}</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedOrder.customerName} · {selectedOrder.customerEmail} · ${selectedOrder.total?.toFixed(2)}
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => { setSelectedOrder(null); setSelectedCard(null); }}>
                      ← Back to queue
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Card preview */}
                  <div className="border border-gray-200 p-6 bg-gray-50">
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-mono">Customer's Card · 1:1 Proof</div>
                    {selectedCard ? (
                      <CardCanvas
                        cardData={selectedCard}
                        tier={selectedCard.tier || 'signature'}
                        watermark={false}
                        scale={1.2}
                        side="both"
                      />
                    ) : (
                      <div className="text-gray-500 italic">No business card linked to this order.</div>
                    )}
                  </div>

                  {/* Items + totals */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-mono">Items</div>
                      <div className="space-y-2">
                        {(selectedOrder.items || []).map((it, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span>{it.productName} × {it.quantity}</span>
                            <span className="font-mono">${it.totalPrice?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-mono">Customer details</div>
                      <div className="text-sm space-y-1">
                        <div><strong>Name:</strong> {selectedCard?.personalName || selectedOrder.customerName}</div>
                        <div><strong>Company:</strong> {selectedCard?.companyName || '—'}</div>
                        <div><strong>Email:</strong> {selectedCard?.email || selectedOrder.customerEmail}</div>
                        <div><strong>Phone:</strong> {selectedCard?.phone || '—'}</div>
                        {selectedCard?.tier && <div><strong>Tier:</strong> {selectedCard.tier}</div>}
                      </div>
                    </div>
                  </div>

                  {/* Status history */}
                  {(selectedOrder.statusHistory || []).length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-mono">Status History</div>
                      <div className="space-y-1 text-sm">
                        {selectedOrder.statusHistory.map((h, i) => (
                          <div key={i} className="flex gap-3 font-mono text-xs">
                            <span className="text-gray-500">{new Date(h.changedAt).toLocaleString()}</span>
                            <span className="uppercase tracking-wider text-blue-600">{h.status}</span>
                            <span className="text-gray-700">{h.notes || ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="border-t pt-6 flex flex-wrap gap-3">
                    {(selectedOrder.status === 'submitted' || selectedOrder.status === 'pre_production') && (
                      <>
                        <Button
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => openTransitionDialog('approve')}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />Approve · Ready for 4over
                        </Button>
                        <Button
                          variant="outline"
                          className="border-amber-500 text-amber-700 hover:bg-amber-50"
                          onClick={() => openTransitionDialog('request_changes')}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />Request Changes
                        </Button>
                        <Button
                          variant="outline"
                          className="border-red-500 text-red-700 hover:bg-red-50"
                          onClick={() => openTransitionDialog('reject')}
                        >
                          <XCircle className="h-4 w-4 mr-2" />Reject
                        </Button>
                      </>
                    )}
                    {selectedOrder.status === 'approved' && (
                      <>
                        <Button
                          className="bg-blue-700 hover:bg-blue-800 text-white"
                          onClick={async () => {
                            const r = await ordersApi.printPdf(selectedOrder.id);
                            if (r?.success && r?.data?.url) {
                              const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '');
                              window.open(`${base}${r.data.url}`, '_blank');
                            } else {
                              alert('Print PDF generation failed · check admin permissions');
                            }
                          }}
                        >
                          <Download className="h-4 w-4 mr-2" />Download Print PDF (CMYK · 4over-ready)
                        </Button>
                        <Button
                          className="bg-amber-600 hover:bg-amber-700"
                          onClick={() => openTransitionDialog('mark_in_production')}
                        >
                          <Truck className="h-4 w-4 mr-2" />Mark · Uploaded to 4over
                        </Button>
                      </>
                    )}
                    {selectedOrder.status === 'in_production' && (
                      <Button
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => openTransitionDialog('mark_shipped')}
                      >
                        <Truck className="h-4 w-4 mr-2" />Mark Shipped + Tracking
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Transition Dialog */}
            <Dialog open={transitionDialog.open} onOpenChange={(o) => setTransitionDialog({ open: o, action: o ? transitionDialog.action : null })}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {transitionDialog.action === 'approve' && '✓ Approve Order'}
                    {transitionDialog.action === 'request_changes' && '↻ Request Changes from Client'}
                    {transitionDialog.action === 'reject' && '✕ Reject Order'}
                    {transitionDialog.action === 'mark_in_production' && '🚛 Mark as Uploaded to 4over'}
                    {transitionDialog.action === 'mark_shipped' && '📦 Mark as Shipped'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  {transitionDialog.action === 'mark_shipped' && (
                    <div>
                      <Label>Tracking Number</Label>
                      <Input
                        value={transitionTracking}
                        onChange={(e) => setTransitionTracking(e.target.value)}
                        placeholder="e.g. 1Z999AA10123456784"
                      />
                    </div>
                  )}
                  <div>
                    <Label>{transitionDialog.action === 'request_changes' ? 'Notes to client (required)' : 'Internal notes (optional)'}</Label>
                    <textarea
                      className="w-full border border-gray-300 p-2 text-sm"
                      rows={4}
                      value={transitionNotes}
                      onChange={(e) => setTransitionNotes(e.target.value)}
                      placeholder={
                        transitionDialog.action === 'request_changes'
                          ? 'Explain what needs to change · the client will see this'
                          : 'Internal note for the audit trail'
                      }
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTransitionDialog({ open: false, action: null })}>
                    Cancel
                  </Button>
                  <Button
                    onClick={submitTransition}
                    disabled={transitionLoading || (transitionDialog.action === 'request_changes' && !transitionNotes.trim())}
                  >
                    {transitionLoading ? 'Saving…' : 'Confirm'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="mlm">
            <MLMConfiguration 
              currentSettings={systemSettings}
              onSettingsChange={handleSystemSettingChange}
            />
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Product & Pricing Management</CardTitle>
                  <Button onClick={openCreateProduct} className="bg-gradient-to-r from-emerald-500 to-blue-600">
                    <Plus className="h-4 w-4 mr-2" />Add Product
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {products.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">No products yet. Click "Add Product" above.</p>
                  )}
                  {products.map((product) => (
                    <div key={product.id} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{product.name || 'Unnamed'}</h3>
                          <p className="text-sm text-gray-600">{product.description || ''}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">{product.category || '-'}</Badge>
                          <Button size="sm" variant="outline" onClick={() => openEditProduct(product)}><Edit className="h-4 w-4" /></Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteProduct(product)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm">Customer Price</Label>
                          <Input type="number" value={product.basePrice ?? 0} className="mt-1" readOnly />
                        </div>
                        <div>
                          <Label className="text-sm">Reseller Price</Label>
                          <Input type="number" value={product.resellerPrice ?? 0} className="mt-1" readOnly />
                        </div>
                        <div>
                          <Label className="text-sm">Commission %</Label>
                          <Input type="number" value={product.commissionRate ?? 0} className="mt-1" readOnly />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Product Create/Edit Dialog */}
                <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                      <div>
                        <Label>Product Name *</Label>
                        <Input value={productDraft.name} onChange={(e) => setProductDraft({ ...productDraft, name: e.target.value })} placeholder="Standard Business Cards (1000)" />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input value={productDraft.description} onChange={(e) => setProductDraft({ ...productDraft, description: e.target.value })} placeholder="Professional cards with USA standard sizing" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Category</Label>
                          <Select value={productDraft.category} onValueChange={(v) => setProductDraft({ ...productDraft, category: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Business Cards">Business Cards</SelectItem>
                              <SelectItem value="Stationery">Stationery</SelectItem>
                              <SelectItem value="Marketing Materials">Marketing Materials</SelectItem>
                              <SelectItem value="Promotional">Promotional</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-end justify-between">
                          <Label>Active</Label>
                          <Switch checked={productDraft.isActive} onCheckedChange={(v) => setProductDraft({ ...productDraft, isActive: v })} />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label>Customer Price ($)</Label>
                          <Input type="number" value={Number.isFinite(productDraft.basePrice) ? productDraft.basePrice : ''} onChange={(e) => setProductDraft({ ...productDraft, basePrice: e.target.value === '' ? 0 : parseFloat(e.target.value) })} />
                        </div>
                        <div>
                          <Label>Reseller Price ($)</Label>
                          <Input type="number" value={Number.isFinite(productDraft.resellerPrice) ? productDraft.resellerPrice : ''} onChange={(e) => setProductDraft({ ...productDraft, resellerPrice: e.target.value === '' ? 0 : parseFloat(e.target.value) })} />
                        </div>
                        <div>
                          <Label>Commission %</Label>
                          <Input type="number" value={Number.isFinite(productDraft.commissionRate) ? productDraft.commissionRate : ''} max="100" onChange={(e) => setProductDraft({ ...productDraft, commissionRate: e.target.value === '' ? 0 : parseInt(e.target.value) })} />
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowProductDialog(false)}>Cancel</Button>
                      <Button onClick={handleSaveProduct}>{editingProduct ? 'Save changes' : 'Create product'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab · WPS-005 visual gallery */}
          <TabsContent value="templates" className="space-y-6">
            <TemplatesGallery />
          </TabsContent>

          {/* Template Editor Tab · WPS-012 */}
          <TabsContent value="tpleditor" className="space-y-6">
            <TemplateEditor />
          </TabsContent>

          {/* Asset Library Tab · WPS-009 */}
          <TabsContent value="assets" className="space-y-6">
            <AssetLibrary />
          </TabsContent>

          {/* Brand Pairings Tab · WPS-015 */}
          <TabsContent value="pairings" className="space-y-6">
            <BrandPairings />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>User & Reseller Management</CardTitle>
                  <Button onClick={openCreateUser} className="bg-gradient-to-r from-emerald-500 to-blue-600">
                    <Plus className="h-4 w-4 mr-2" />Add User / Reseller
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {resellers.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">No resellers yet. Click "Add User / Reseller" above.</p>
                  )}
                  {resellers.map((reseller) => {
                    const fullName = reseller.name || `${reseller.firstName || ''} ${reseller.lastName || ''}`.trim() || reseller.email || 'User';
                    const initials = fullName.split(' ').filter(Boolean).map(n => n[0] || '').join('').toUpperCase() || '?';
                    const level = reseller.level || reseller.resellerLevel || 'Bronze';
                    const teamSize = reseller.teamSize ?? 0;
                    const status = reseller.status || (reseller.isActive ? 'active' : 'inactive');
                    return (
                    <div key={reseller.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                          {initials}
                        </div>
                        <div>
                          <h3 className="font-semibold">{fullName}</h3>
                          <p className="text-sm text-gray-600">{reseller.email}</p>
                          <p className="text-xs text-gray-500">Level: {level} | Team: {teamSize}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Badge variant={status === 'active' ? 'default' : 'secondary'}>
                          {status}
                        </Badge>
                        <Button size="sm" variant="outline" onClick={() => openEditUser(reseller)} title="Edit">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteUser(reseller)} title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* User Create/Edit Dialog */}
                <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingUser ? 'Edit User / Reseller' : 'Add New User / Reseller'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>First name</Label>
                          <Input value={userDraft.firstName} onChange={(e) => setUserDraft({ ...userDraft, firstName: e.target.value })} />
                        </div>
                        <div>
                          <Label>Last name</Label>
                          <Input value={userDraft.lastName} onChange={(e) => setUserDraft({ ...userDraft, lastName: e.target.value })} />
                        </div>
                      </div>
                      <div>
                        <Label>Email {!canEditEmails && editingUser && <span className="text-xs text-gray-400">(read-only — general admin access required)</span>}</Label>
                        <Input
                          type="email"
                          value={userDraft.email}
                          onChange={(e) => setUserDraft({ ...userDraft, email: e.target.value })}
                          disabled={!!editingUser && !canEditEmails}
                        />
                        {editingUser && canEditEmails && (
                          <p className="text-xs text-emerald-600 mt-1">⚡ General Admin: you can edit emails — must remain unique</p>
                        )}
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input value={userDraft.phone} onChange={(e) => setUserDraft({ ...userDraft, phone: e.target.value })} placeholder="+12149497999" />
                      </div>
                      {!editingUser && (
                        <div>
                          <Label>Password (required for new user)</Label>
                          <Input type="password" value={userDraft.password} onChange={(e) => setUserDraft({ ...userDraft, password: e.target.value })} />
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>User Type</Label>
                          <Select value={userDraft.userType} onValueChange={(v) => setUserDraft({ ...userDraft, userType: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="customer">Customer</SelectItem>
                              <SelectItem value="reseller">Reseller</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {userDraft.userType === 'reseller' && (
                          <div>
                            <Label>Reseller Level</Label>
                            <Select value={userDraft.resellerLevel} onValueChange={(v) => setUserDraft({ ...userDraft, resellerLevel: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Bronze">Bronze</SelectItem>
                                <SelectItem value="Silver">Silver</SelectItem>
                                <SelectItem value="Gold">Gold</SelectItem>
                                <SelectItem value="Platinum">Platinum</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Active</Label>
                        <Switch checked={userDraft.isActive} onCheckedChange={(v) => setUserDraft({ ...userDraft, isActive: v })} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
                      <Button onClick={handleSaveUser}>{editingUser ? 'Save changes' : 'Create user'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chat Management Tab */}
          <TabsContent value="chat">
            <ChatManagement />
          </TabsContent>

          {/* Theme Customizer Tab */}
          <TabsContent value="theme">
            <ThemeCustomizer 
              settings={systemSettings}
              onSettingsChange={handleSystemSettingChange}
            />
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <PaymentSettings />
          </TabsContent>

          {/* Add-ons Tab */}
          <TabsContent value="addons">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Premium Add-ons</CardTitle>
                  <Button onClick={openCreateAddon} className="bg-gradient-to-r from-emerald-500 to-blue-600">
                    <Plus className="h-4 w-4 mr-2" />Add Premium Add-on
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-2">Upsell options ofrecidos al cliente al checkout. Margins típicos 75-85%.</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {addons.length === 0 && <p className="text-sm text-gray-500 text-center py-8">No add-ons yet. Click "Add Premium Add-on".</p>}
                  {addons.map(a => {
                    const margin = a.price > 0 ? Math.round(((a.price - (a.cost || 0)) / a.price) * 100) : 0;
                    return (
                      <div key={a.id} className="p-4 border border-gray-200 rounded-lg flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-semibold">{a.name}</h3>
                            <Badge variant="outline" className="text-xs">{a.category}</Badge>
                            <Badge className={margin >= 70 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>{margin}% margin</Badge>
                          </div>
                          <p className="text-sm text-gray-600">{a.description}</p>
                          <p className="text-xs text-gray-500 mt-1">Price: ${(a.price || 0).toFixed(2)} · Cost: ${(a.cost || 0).toFixed(2)} · Profit: ${((a.price || 0) - (a.cost || 0)).toFixed(2)}</p>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <Badge variant={a.isActive ? 'default' : 'secondary'}>{a.isActive ? 'Active' : 'Inactive'}</Badge>
                          <Button size="sm" variant="outline" onClick={() => openEditAddon(a)}><Edit className="h-4 w-4" /></Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteAddon(a)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Dialog open={showAddonDialog} onOpenChange={setShowAddonDialog}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingAddon ? 'Edit Add-on' : 'New Premium Add-on'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                      <div>
                        <Label>Name *</Label>
                        <Input value={addonDraft.name} onChange={(e) => setAddonDraft({ ...addonDraft, name: e.target.value })} placeholder="Foil Stamp Upgrade" />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input value={addonDraft.description} onChange={(e) => setAddonDraft({ ...addonDraft, description: e.target.value })} placeholder="What does this add-on do?" />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label>Price ($)</Label>
                          <Input type="number" value={Number.isFinite(addonDraft.price) ? addonDraft.price : ''} onChange={(e) => setAddonDraft({ ...addonDraft, price: e.target.value === '' ? 0 : parseFloat(e.target.value) })} />
                        </div>
                        <div>
                          <Label>Cost ($)</Label>
                          <Input type="number" value={Number.isFinite(addonDraft.cost) ? addonDraft.cost : ''} onChange={(e) => setAddonDraft({ ...addonDraft, cost: e.target.value === '' ? 0 : parseFloat(e.target.value) })} />
                        </div>
                        <div>
                          <Label>Category</Label>
                          <Select value={addonDraft.category} onValueChange={(v) => setAddonDraft({ ...addonDraft, category: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="finish">Finish</SelectItem>
                              <SelectItem value="presentation">Presentation</SelectItem>
                              <SelectItem value="tech">Tech</SelectItem>
                              <SelectItem value="service">Service</SelectItem>
                              <SelectItem value="fulfillment">Fulfillment</SelectItem>
                              <SelectItem value="quantity">Quantity</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Active</Label>
                        <Switch checked={addonDraft.isActive} onCheckedChange={(v) => setAddonDraft({ ...addonDraft, isActive: v })} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowAddonDialog(false)}>Cancel</Button>
                      <Button onClick={handleSaveAddon}>{editingAddon ? 'Save' : 'Create'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>B2B Partner Program</CardTitle>
                  <Button onClick={openCreatePartner} className="bg-gradient-to-r from-emerald-500 to-blue-600">
                    <Plus className="h-4 w-4 mr-2" />Add Partner
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-2">Brand consultants, wedding planners, real estate brokerages que refieren clientes con wholesale discount. Separado del MLM clásico.</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {partners.length === 0 && <p className="text-sm text-gray-500 text-center py-8">No partners yet. Click "Add Partner".</p>}
                  {partners.map(p => (
                    <div key={p.id} className="p-4 border border-gray-200 rounded-lg flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold">{p.businessName}</h3>
                          <Badge variant="outline" className="text-xs">{(p.type || '').replace('_', ' ')}</Badge>
                          <Badge className="bg-purple-100 text-purple-800">{p.wholesaleDiscount}% wholesale</Badge>
                        </div>
                        <p className="text-sm text-gray-600">{p.contactName} · {p.email} · {p.phone}</p>
                        <p className="text-xs text-gray-500 mt-1">Min monthly orders: {p.minMonthlyOrders}</p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <Badge variant={p.isActive ? 'default' : 'secondary'}>{p.isActive ? 'Active' : 'Inactive'}</Badge>
                        <Button size="sm" variant="outline" onClick={() => openEditPartner(p)}><Edit className="h-4 w-4" /></Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeletePartner(p)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>

                <Dialog open={showPartnerDialog} onOpenChange={setShowPartnerDialog}>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingPartner ? 'Edit Partner' : 'New B2B Partner'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                      <div>
                        <Label>Business Name *</Label>
                        <Input value={partnerDraft.businessName} onChange={(e) => setPartnerDraft({ ...partnerDraft, businessName: e.target.value })} placeholder="Acme Brand Studio" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Contact Name</Label>
                          <Input value={partnerDraft.contactName} onChange={(e) => setPartnerDraft({ ...partnerDraft, contactName: e.target.value })} />
                        </div>
                        <div>
                          <Label>Type</Label>
                          <Select value={partnerDraft.type} onValueChange={(v) => setPartnerDraft({ ...partnerDraft, type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="brand_consultant">Brand Consultant</SelectItem>
                              <SelectItem value="wedding_planner">Wedding Planner</SelectItem>
                              <SelectItem value="real_estate">Real Estate Brokerage</SelectItem>
                              <SelectItem value="photographer">Photographer</SelectItem>
                              <SelectItem value="web_designer">Web Designer</SelectItem>
                              <SelectItem value="agency">Marketing Agency</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Email *</Label>
                          <Input type="email" value={partnerDraft.email} onChange={(e) => setPartnerDraft({ ...partnerDraft, email: e.target.value })} />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input value={partnerDraft.phone} onChange={(e) => setPartnerDraft({ ...partnerDraft, phone: e.target.value })} placeholder="+12149497999" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Wholesale Discount (%)</Label>
                          <Input type="number" value={Number.isFinite(partnerDraft.wholesaleDiscount) ? partnerDraft.wholesaleDiscount : ''} onChange={(e) => setPartnerDraft({ ...partnerDraft, wholesaleDiscount: e.target.value === '' ? 0 : parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <Label>Min Monthly Orders</Label>
                          <Input type="number" value={Number.isFinite(partnerDraft.minMonthlyOrders) ? partnerDraft.minMonthlyOrders : ''} onChange={(e) => setPartnerDraft({ ...partnerDraft, minMonthlyOrders: e.target.value === '' ? 0 : parseInt(e.target.value) })} />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Active</Label>
                        <Switch checked={partnerDraft.isActive} onCheckedChange={(v) => setPartnerDraft({ ...partnerDraft, isActive: v })} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowPartnerDialog(false)}>Cancel</Button>
                      <Button onClick={handleSavePartner}>{editingPartner ? 'Save' : 'Create'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>System Configuration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={systemSettings.companyName}
                      onChange={(e) => handleSystemSettingChange("companyName", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Reseller Upgrade Fee ($)</Label>
                    <Input
                      type="number"
                      value={systemSettings.resellerUpgradeFee}
                      onChange={(e) => handleSystemSettingChange("resellerUpgradeFee", parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Enable MLM System</Label>
                    <Switch
                      checked={systemSettings.enableMLM}
                      onCheckedChange={(checked) => handleSystemSettingChange("enableMLM", checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Enable AI Chat Support</Label>
                    <Switch
                      checked={systemSettings.enableChat}
                      onCheckedChange={(checked) => handleSystemSettingChange("enableChat", checked)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Credits & Attribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
                    <div className="text-center">
                      <Brush className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Design and Development
                      </h3>
                      <p className="text-blue-700 font-medium text-lg mb-1">
                        Wunli.com
                      </p>
                      <p className="text-gray-600 text-sm">
                        Digital Software Solutions
                      </p>
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          © 2024 - Professional MLM Business Card Platform
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EnhancedAdminDashboard;